import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { formatPct, pctForBar } from '@/lib/format-pct';

const STOREFRONT_NOTE =
	'Your Local Storefront needs at least a title to make a product searchable and orderable. Though, adding richer product data will increase your impact.';
const OTHER_CHANNELS_NOTE =
	'Each of these channels has its own requirements (Meta wants title, description and image; delivery channels also need categories), so the bar here is stricter than Local Storefront’s title floor or Google’s catalogue lookup.';
const GOOGLE_NOTE = "Google Shopping typically shows 95%+ of in-stock products — it accepts simpler profiles, so it's tracked separately.";
// Surfaced only when the headline percentage tops 100% (Google's accepted count
// briefly outruns our in-stock view because GMC's catalogue refresh is slower
// than our Typesense stock flag). Explains the lag instead of normalising it
// away — see NEA-5880.
const HEADLINE_OVER_100_NOTE =
	'Google can briefly list more products than we currently mark as in stock — its catalogue refreshes on its own schedule, so listings stay active for a few hours after a product runs out. The numbers reconcile on its next refresh.';

function ChannelRow({ label, sub, pct, tooltip, warn }) {
	return (
		<div>
			<div className="mb-1.5 flex items-baseline justify-between gap-3">
				<div className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground">
					{warn ? <AlertTriangle className="h-3.5 w-3.5 text-warning-soft-foreground" /> : null}
					{label}
					{sub ? <span className="ml-1 text-xs font-normal text-muted-foreground">{sub}</span> : null}
					{tooltip ? (
						<Tooltip>
							<TooltipTrigger asChild>
								<button
									type="button"
									className="text-muted-foreground transition hover:text-foreground"
									aria-label="More info"
								>
									<Info className="h-3.5 w-3.5" />
								</button>
							</TooltipTrigger>
							<TooltipContent className="max-w-xs text-xs leading-relaxed">{tooltip}</TooltipContent>
						</Tooltip>
					) : null}
				</div>
				<div className={`text-sm font-bold tabular-nums ${warn ? 'text-warning-soft-foreground' : ''}`}>{pct}%</div>
			</div>
			<div className="relative h-2 w-full overflow-hidden rounded-full bg-muted">
				<div
					className={`absolute inset-y-0 left-0 rounded-full ${warn ? 'bg-warning' : 'bg-success'}`}
					style={{ width: `${pct}%` }}
				/>
			</div>
		</div>
	);
}

export function VisibilityHeaderCard({ visibility, inStockItems, showChannelBreakdown = true, gmcProductStats, staleDays = null }) {
	// A frozen POS feed (≥ RECONNECT_THRESHOLD_DAYS without an ingest) means the
	// counts below describe the shop as it was at the last sync, not today — a
	// 355-day-disconnected shop was reading "100% visible" (NEA-5934). Replace
	// the claim with an honest outdated state instead of dressing stale numbers
	// in green.
	if (staleDays != null) {
		return (
			<SoftCard className="border-warning-soft-foreground/20 bg-warning-soft/40 !p-5 md:!p-6">
				<div className="min-w-0">
					<div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-warning-soft-foreground">
						<AlertTriangle className="h-3.5 w-3.5" />
						Stock data outdated
					</div>
					<div className="mt-2 flex flex-wrap items-baseline gap-2">
						<span className="text-3xl font-extrabold tracking-tight tabular-nums text-warning-soft-foreground md:text-4xl">
							{staleDays.toLocaleString()} days
						</span>
						<span className="text-sm tabular-nums text-muted-foreground">since your last stock sync</span>
					</div>
					<p className="mt-2 text-xs text-warning-soft-foreground">
						These figures reflect your shop back then, not today. Reconnect your POS to bring your stock back up to date.
					</p>
				</div>
			</SoftCard>
		);
	}

	const gmcActive = gmcProductStats?.active ?? null;
	const gmcDisapproved = gmcProductStats?.disapproved ?? 0;
	const hasGmcIssues = gmcActive !== null && gmcDisapproved > 0;
	const allGmcDisapproved = hasGmcIssues && gmcActive === 0;

	// When scoped to a single channel, switch the card to a warning state
	// if GMC has disapproved everything or the catalogue is empty.
	const emptyFeed = !showChannelBreakdown && (inStockItems ?? 0) === 0 && (visibility?.visibleCount ?? 0) === 0;
	const warnState = (!showChannelBreakdown && allGmcDisapproved) || emptyFeed;
	// On the Google channel page, use GMC's active count (what Google actually
	// accepted) rather than the Typesense count (what we sent).
	const useGmcCount = !showChannelBreakdown && gmcActive !== null;
	const visible = warnState ? 0 : useGmcCount ? gmcActive : (visibility?.visibleCount ?? 0);
	const total = inStockItems ?? 0;
	const visiblePctLabel = formatPct(visible, total);
	const visibleBarPct = pctForBar(visible, total);
	// True when the **displayed** headline percentage rounds above 100% on the
	// Google channel manage page. Gated structurally on `useGmcCount` so the
	// Google-specific lag copy can't fire on an Inventory-page render where
	// `visibleCount` (Typesense) happens to drift above `inStockItems` —
	// numerically possible, semantically wrong context. Matching the rounded
	// value rather than the raw ratio means e.g. 433/432 (100.23%) reads as
	// 100% and stays silent; 1006/1000 (100.6%) rounds to 101% and shows.
	const headlineExceedsHundred = useGmcCount && total > 0 && Math.round((visible / total) * 100) > 100;

	const StatusIcon = warnState ? AlertTriangle : CheckCircle2;
	const cardBorder = warnState ? 'border-warning/30' : 'border-success-soft-foreground/20';
	const cardBg = warnState ? 'bg-warning-soft/40' : 'bg-success-soft/40';
	const accentColor = warnState ? 'text-warning-soft-foreground' : 'text-success-soft-foreground';
	const barColor = warnState ? 'bg-warning' : 'bg-success';
	const headerLabel = warnState ? 'Not visible to shoppers' : 'Visible to shoppers';

	return (
		<TooltipProvider delayDuration={150}>
			<SoftCard className={`${cardBorder} ${cardBg} !p-5 md:!p-6`}>
				<div className="min-w-0">
					<div className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ${accentColor}`}>
						<StatusIcon className="h-3.5 w-3.5" />
						{headerLabel}
					</div>
					<div className="mt-2 flex flex-wrap items-baseline gap-2">
						<span className={`text-3xl font-extrabold tracking-tight tabular-nums ${accentColor} md:text-4xl`}>
							{visible.toLocaleString()}
						</span>
						<span className="inline-flex items-center gap-1 text-sm tabular-nums text-muted-foreground">
							<span>
								of {total.toLocaleString()} in-stock products · {visiblePctLabel}
							</span>
							{headlineExceedsHundred ? (
								<Tooltip>
									<TooltipTrigger asChild>
										<button
											type="button"
											className="text-muted-foreground transition hover:text-foreground"
											aria-label="Why is this above 100%?"
										>
											<Info className="h-3.5 w-3.5" />
										</button>
									</TooltipTrigger>
									<TooltipContent className="max-w-xs text-xs leading-relaxed">{HEADLINE_OVER_100_NOTE}</TooltipContent>
								</Tooltip>
							) : null}
						</span>
					</div>
					{warnState ? (
						<p className="mt-2 text-xs text-warning-soft-foreground">
							{emptyFeed && !allGmcDisapproved
								? 'No products are being sent to this channel. Check your inventory connection.'
								: 'Google is disapproving all submitted products. Contact support@unify.near.st for help.'}
						</p>
					) : null}
				</div>

				{showChannelBreakdown ? (
					<div className="mt-5 space-y-3">
						{visibility?.storefront ? (
							<ChannelRow label="Local Storefront" pct={visibility.storefront.pct} tooltip={STOREFRONT_NOTE} />
						) : null}
						{visibility?.other ? (
							<ChannelRow
								label="Other channels"
								sub={visibility.other.channels.map((c) => c.label).join(', ')}
								pct={visibility.other.pct}
								tooltip={OTHER_CHANNELS_NOTE}
							/>
						) : null}
						{visibility?.google ? (
							<ChannelRow
								label="Google"
								// GMC's active count can briefly exceed in-stock: products just gone out of stock
								// stay active in Google's catalogue until the next sync. Cap at 100% so the bar
								// stays sensible in a "X of in-stock" reading. `pctForBar` returns 0 when total
								// is 0, so we don't render a full bar against an empty in-stock denominator.
								pct={gmcActive !== null ? Math.min(pctForBar(gmcActive, total), 100) : visibility.google.pct}
								tooltip={GOOGLE_NOTE}
							/>
						) : null}
					</div>
				) : (
					<div className="mt-4">
						<div className="relative h-2 w-full overflow-hidden rounded-full bg-white/70">
							<div className={`absolute inset-y-0 left-0 rounded-full ${barColor}`} style={{ width: `${visibleBarPct}%` }} />
						</div>
					</div>
				)}
			</SoftCard>
		</TooltipProvider>
	);
}
